'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PaymentCalculator() {
  const t = useTranslations("tools.paymentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentMode, setCurrentMode] = useState<'fixed-term' | 'fixed-payment'>('fixed-term');
  const [inputs, setInputs] = useState({
    loanAmount: 200000,
    loanTerm: 15,
    monthlyPayment: 2000,
    interestRate: 6,
  });
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabsContainer: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginBottom: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' as const },
      {},
      { marginBottom: '2rem' },
      {}
    ),
    tabButton: (isActive: boolean) => getResponsiveStyle(
      {
        background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        color: isActive ? 'white' : '#64748b',
        border: isActive ? 'none' : '2px solid #e2e8f0',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      },
      { padding: '0.75rem 1.5rem', fontSize: '0.9rem' },
      { padding: '0.75rem 2rem', fontSize: '1rem' },
      { padding: '0.875rem 2.5rem', fontSize: '1rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(2, 1fr)' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        background: '#f8fafc',
        width: '100%',
      },
      { padding: '0.75rem 1rem', fontSize: '1rem' },
      { padding: '0.875rem 1rem', fontSize: '1rem' },
      { padding: '0.875rem 1rem', fontSize: '1rem' }
    ),
    inputWrapper: {
      position: 'relative' as const,
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
    },
    inputSuffix: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    primaryResult: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        borderRadius: '1rem',
        textAlign: 'center' as const,
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    primaryResultLabel: getResponsiveStyle(
      { opacity: 0.9, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    primaryResultValue: getResponsiveStyle(
      { fontWeight: 700 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    resultItem: {
      background: '#f8fafc',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb',
    },
    resultLabel: getResponsiveStyle(
      { display: 'block', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 },
      { fontSize: '0.875rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.875rem' }
    ),
    resultValue: getResponsiveStyle(
      { display: 'block', color: colors.textPrimary, fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    chartContainer: {
      margin: '2rem 0',
      textAlign: 'center' as const,
    },
    chartLegend: getResponsiveStyle(
      { display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' as const },
      {},
      {},
      {}
    ),
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    legendColor: {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '2rem',
    },
    th: getResponsiveStyle(
      { padding: '1rem', textAlign: 'left' as const, background: '#f8fafc', fontWeight: 700, color: '#334155', borderBottom: '1px solid #e2e8f0' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    td: getResponsiveStyle(
      { padding: '1rem', textAlign: 'left' as const, borderBottom: '1px solid #e2e8f0' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (currentMode === 'fixed-term') {
      calculateFixedTerm();
    } else {
      calculateFixedPayment();
    }
  };

  const calculateFixedTerm = () => {
    const principal = inputs.loanAmount;
    const annualRate = inputs.interestRate;
    const years = inputs.loanTerm;
    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;

    let monthlyPayment;
    if (monthlyRate === 0) {
      monthlyPayment = principal / numPayments;
    } else {
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
                      (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - principal;

    const principalPercent = Math.round((principal / totalPayment) * 100);
    const interestPercent = 100 - principalPercent;

    const amortizationSchedule = generateAmortizationSchedule(principal, monthlyPayment, monthlyRate, numPayments);

    setResults({
      mode: 'fixed-term',
      monthlyPayment,
      totalPayment,
      totalInterest,
      principalPercent,
      interestPercent,
      amortizationSchedule,
    });
  };

  const calculateFixedPayment = () => {
    const principal = inputs.loanAmount;
    const monthlyPayment = inputs.monthlyPayment;
    const annualRate = inputs.interestRate;
    const monthlyRate = annualRate / 100 / 12;

    if (monthlyPayment <= principal * monthlyRate) {
      alert(t('paymentTooLowError'));
      return;
    }

    let numPayments;
    if (monthlyRate === 0) {
      numPayments = principal / monthlyPayment;
    } else {
      numPayments = Math.log(monthlyPayment / (monthlyPayment - principal * monthlyRate)) /
                   Math.log(1 + monthlyRate);
    }

    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - principal;
    const years = Math.floor(numPayments / 12);
    const months = Math.ceil(numPayments % 12);

    const principalPercent = Math.round((principal / totalPayment) * 100);
    const interestPercent = 100 - principalPercent;

    const amortizationSchedule = generateAmortizationSchedule(principal, monthlyPayment, monthlyRate, numPayments);

    setResults({
      mode: 'fixed-payment',
      monthlyPayment,
      totalPayment,
      totalInterest,
      numPayments: Math.ceil(numPayments),
      years,
      months,
      principalPercent,
      interestPercent,
      amortizationSchedule,
    });
  };

  const generateAmortizationSchedule = (principal: number, payment: number, monthlyRate: number, totalPayments: number) => {
    const schedule = [];
    let balance = principal;
    const maxRows = Math.min(12, Math.ceil(totalPayments));

    for (let i = 1; i <= maxRows; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = payment - interestPayment;
      balance -= principalPayment;

      if (balance < 0) balance = 0;

      schedule.push({
        month: i,
        payment,
        principal: principalPayment,
        interest: interestPayment,
        balance,
      });
    }

    return schedule;
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return [
      'M', x, y,
      'L', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      'Z'
    ].join(' ');
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.tabsContainer}>
            <button
              style={styles.tabButton(currentMode === 'fixed-term')}
              onClick={() => setCurrentMode('fixed-term')}
              onMouseEnter={(e) => {
                if (currentMode !== 'fixed-term') {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.color = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (currentMode !== 'fixed-term') {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              {t('fixedTermTab')}
            </button>
            <button
              style={styles.tabButton(currentMode === 'fixed-payment')}
              onClick={() => setCurrentMode('fixed-payment')}
              onMouseEnter={(e) => {
                if (currentMode !== 'fixed-payment') {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.color = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (currentMode !== 'fixed-payment') {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              {t('fixedPaymentTab')}
            </button>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="loan-amount">{t('loanAmountLabel')}</label>
                <div style={styles.inputWrapper}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="number"
                    id="loan-amount"
                    value={inputs.loanAmount}
                    onChange={(e) => setInputs({...inputs, loanAmount: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="1000"
                    required
                    style={{...styles.input, paddingLeft: '2rem'}}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.background = 'white';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.background = '#f8fafc';
                    }}
                  />
                </div>
              </div>

              {currentMode === 'fixed-term' && (
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="loan-term">{t('loanTermLabel')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="loan-term"
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({...inputs, loanTerm: parseFloat(e.target.value) || 0})}
                      min="1"
                      max="50"
                      step="1"
                      required
                      style={{...styles.input, paddingRight: '3.5rem'}}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                      }}
                    />
                    <span style={styles.inputSuffix}>{t('yearsUnit')}</span>
                  </div>
                </div>
              )}

              {currentMode === 'fixed-payment' && (
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="monthly-payment">{t('monthlyPaymentLabel')}</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="monthly-payment"
                      value={inputs.monthlyPayment}
                      onChange={(e) => setInputs({...inputs, monthlyPayment: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="10"
                      required
                      style={{...styles.input, paddingLeft: '2rem'}}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="interest-rate">{t('interestRateLabel')}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="interest-rate"
                    value={inputs.interestRate}
                    onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="100"
                    step="0.1"
                    required
                    style={{...styles.input, paddingRight: '2.5rem'}}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.background = 'white';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.background = '#f8fafc';
                    }}
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>
            </div>

            <button
              type="submit"
              style={styles.calculateButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              {currentMode === 'fixed-term' ? t('calculatePaymentButton') : t('calculateTimeButton')}
            </button>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={{...styles.h2, textAlign: 'center'}}>{t('resultsTitle')}</h3>

              <div style={styles.primaryResult}>
                <div style={styles.primaryResultLabel}>
                  {results.mode === 'fixed-term' ? t('monthlyPaymentResult') : t('timeToPayOffResult')}
                </div>
                <div style={styles.primaryResultValue}>
                  {results.mode === 'fixed-term'
                    ? `$${results.monthlyPayment.toFixed(2)}`
                    : `${results.years > 0 ? `${results.years} ${results.years > 1 ? t('yearsUnit') : t('yearUnit')}` : ''} ${results.months > 0 ? `${results.months} ${results.months > 1 ? t('monthsUnit') : t('monthUnit')}` : ''}`
                  }
                </div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('totalPaymentsLabel')}</span>
                  <span style={styles.resultValue}>${results.totalPayment.toFixed(2)}</span>
                </div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('totalInterestLabel')}</span>
                  <span style={styles.resultValue}>${results.totalInterest.toFixed(2)}</span>
                </div>
                {results.mode === 'fixed-payment' && (
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t('numberOfPaymentsLabel')}</span>
                    <span style={styles.resultValue}>{results.numPayments} {t('paymentsUnit')}</span>
                  </div>
                )}
              </div>

              <div style={styles.chartContainer}>
                <svg width="220" height="220" style={{display: 'inline-block', margin: '1rem auto'}}>
                  <path
                    d={describeArc(110, 110, 80, 0, (results.principalPercent / 100) * 360)}
                    fill="#2563eb"
                  />
                  <path
                    d={describeArc(110, 110, 80, (results.principalPercent / 100) * 360, 360)}
                    fill="#f59e0b"
                  />
                  <circle cx="110" cy="110" r="40" fill="white" />
                </svg>
                <div style={styles.chartLegend}>
                  <div style={styles.legendItem}>
                    <div style={{...styles.legendColor, background: '#2563eb'}}></div>
                    <span>{t('principalLegend')} ({results.principalPercent}%)</span>
                  </div>
                  <div style={styles.legendItem}>
                    <div style={{...styles.legendColor, background: '#f59e0b'}}></div>
                    <span>{t('interestLegend')} ({results.interestPercent}%)</span>
                  </div>
                </div>
              </div>

              <div style={{overflowX: 'auto'}}>
                <h3 style={{...styles.h2, marginBottom: '1rem'}}>{t('amortizationScheduleTitle')}</h3>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t('monthColumn')}</th>
                      <th style={styles.th}>{t('paymentColumn')}</th>
                      <th style={styles.th}>{t('principalColumn')}</th>
                      <th style={styles.th}>{t('interestColumn')}</th>
                      <th style={styles.th}>{t('balanceColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.amortizationSchedule.map((row: any, index: number) => (
                      <tr key={index} style={{background: index % 2 === 0 ? 'transparent' : '#f8fafc'}}>
                        <td style={styles.td}>{row.month}</td>
                        <td style={styles.td}>${row.payment.toFixed(2)}</td>
                        <td style={styles.td}>${row.principal.toFixed(2)}</td>
                        <td style={styles.td}>${row.interest.toFixed(2)}</td>
                        <td style={styles.td}>${row.balance.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('insightsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightsPoint1')}</li>
          <li style={styles.listItem}>{t('insightsPoint2')}</li>
          <li style={styles.listItem}>{t('insightsPoint3')}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('insightsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightsPoint4')}</li>
          <li style={styles.listItem}>{t('insightsPoint5')}</li>
          <li style={styles.listItem}>{t('insightsPoint6')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('bestPracticesTitle')}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('bestPracticesSubtitle1')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('bestPracticesPoint1')}</li>
          <li style={styles.listItem}>{t('bestPracticesPoint2')}</li>
          <li style={styles.listItem}>{t('bestPracticesPoint3')}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('bestPracticesSubtitle2')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('bestPracticesPoint4')}</li>
          <li style={styles.listItem}>{t('bestPracticesPoint5')}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('bestPracticesSubtitle3')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('bestPracticesPoint6')}</li>
          <li style={styles.listItem}>{t('bestPracticesPoint7')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion1')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer1')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion2')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer2')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion3')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer3')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion4')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer4')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion5')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer5')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a
            href="https://www.calculator.net/payment-calculator.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            Payment Calculator – Calculator.net
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.pmcu.org/calculators/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            Financial Calculators – Pacific Marine Credit Union
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://finaid.org/calculators/prepayment/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            Prepayment Calculator – FinAid.org
          </a>
        </div>
      </section>
    </div>
  );
}